import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './components/AdminLayout';
import Dashboard from './pages/Dashboard/Dashboard';
import Users from './pages/Users/Users';
import AddUser from './pages/Users/AddUser';
import EditUser from './pages/Users/EditUser';
import TrashedUsers from './pages/Users/TrashedUsers';
import Roles from './pages/Roles/Roles';
import AddRole from './pages/Roles/AddRole';
import EditRole from './pages/Roles/EditRole';
import TrashedRoles from './pages/Roles/TrashedRoles';
import ContactLeads from './pages/ContactLeads/ContactLeads';
import EditLead from './pages/ContactLeads/EditLead';
import TrashedLeads from './pages/ContactLeads/TrashedLeads';
import LeadStatus from './pages/LeadStatus/LeadStatus';
import AddStatus from './pages/LeadStatus/AddStatus';
import EditStatus from './pages/LeadStatus/EditStatus';
import TrashedLeadStatus from './pages/LeadStatus/TrashedLeadStatus';
import Services from './pages/Services/Services';
import AddService from './pages/Services/AddService';
import EditService from './pages/Services/EditService';
import TrashedServices from './pages/Services/TrashedServices';
import Blogs from './pages/Blogs/Blogs';
import AddBlog from './pages/Blogs/AddBlog';
import EditBlog from './pages/Blogs/EditBlog';
import TrashedBlogs from './pages/Blogs/TrashedBlogs';
import BlogAnalytics from './pages/Blogs/BlogAnalytics';
import BlogCategories from './pages/BlogCategories/BlogCategories';
import AddBlogCategory from './pages/BlogCategories/AddBlogCategory';
import EditBlogCategory from './pages/BlogCategories/EditBlogCategory';
import TrashedBlogCategories from './pages/BlogCategories/TrashedBlogCategories';
import Skills from './pages/Skills/Skills';
import AddSkill from './pages/Skills/AddSkill';
import EditSkill from './pages/Skills/EditSkill';
import TrashedSkills from './pages/Skills/TrashedSkills';
import FAQs from './pages/FAQs/FAQs';
import AddFAQ from './pages/FAQs/AddFAQ';
import EditFAQ from './pages/FAQs/EditFAQ';
import TrashedFAQs from './pages/FAQs/TrashedFAQs';
import Testimonials from './pages/Testimonials/Testimonials';
import AddTestimonial from './pages/Testimonials/AddTestimonial';
import EditTestimonial from './pages/Testimonials/EditTestimonial';
import TrashedTestimonials from './pages/Testimonials/TrashedTestimonials';
import HeaderFooter from './pages/HeaderFooter/HeaderFooter';
import AddHeaderFooter from './pages/HeaderFooter/AddHeaderFooter';
import EditHeaderFooter from './pages/HeaderFooter/EditHeaderFooter';
import TrashedHeaderFooter from './pages/HeaderFooter/TrashedHeaderFooter';
import SEOSettings from './pages/SEOSettings/SEOSettings';
import EditSEOSetting from './pages/SEOSettings/EditSEOSetting';
import NotificationSettings from './pages/NotificationSettings/NotificationSettings';
import TermConditions from './pages/TermConditions/TermConditions';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import Settings from './pages/Settings/Settings';
import WorkEducation from './pages/WorkEducation/WorkEducation';
import AddWorkEducation from './pages/WorkEducation/AddWorkEducation';
import EditWorkEducation from './pages/WorkEducation/EditWorkEducation';
import TrashedWorkEducation from './pages/WorkEducation/TrashedWorkEducation';
import Clients from './pages/Clients/Clients';
import AddClient from './pages/Clients/AddClient';
import EditClient from './pages/Clients/EditClient';
import TrashedClients from './pages/Clients/TrashedClients';
import Projects from './pages/Projects/Projects';
import AddProject from './pages/Projects/AddProject';
import EditProject from './pages/Projects/EditProject';
import TrashedProjects from './pages/Projects/TrashedProjects';
import NewsletterSubscribers from './pages/NewsletterSubscribers/NewsletterSubscribers';
import NewsletterAnalytics from './pages/NewsletterSubscribers/NewsletterAnalytics';
import JobListPage from './pages/Jobs/JobListPage';
import NotFound from './pages/NotFound/NotFound';
import LoginPage from './pages/Auth/LoginPage';
import EmailVerificationPage from './pages/Auth/EmailVerificationPage';
import AccessDeniedPage from './pages/Auth/AccessDeniedPage';
import SkeletonLoader from './components/SkeletonLoader';
import './App.css';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <SkeletonLoader type="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user && !user.email_verified_at) {
    return <Navigate to="/email/verify" />;
  }

  return children;
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/email/verify" element={<EmailVerificationPage />} />

            <Route path="/" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="users/create" element={<AddUser />} />
              <Route path="users/:id/edit" element={<EditUser />} />
              <Route path="users/trash" element={<TrashedUsers />} />
              <Route path="roles" element={<Roles />} />
              <Route path="roles/create" element={<AddRole />} />
              <Route path="roles/:id/edit" element={<EditRole />} />
              <Route path="roles/trash" element={<TrashedRoles />} />
              <Route path="contact-leads" element={<ContactLeads />} />
              <Route path="contact-leads/:id/edit" element={<EditLead />} />
              <Route path="contact-leads/trash" element={<TrashedLeads />} />
              <Route path="lead-status" element={<LeadStatus />} />
              <Route path="lead-status/create" element={<AddStatus />} />
              <Route path="lead-status/:id/edit" element={<EditStatus />} />
              <Route path="lead-status/trash" element={<TrashedLeadStatus />} />
              <Route path="services" element={<Services />} />
              <Route path="services/create" element={<AddService />} />
              <Route path="services/:id/edit" element={<EditService />} />
              <Route path="services/trash" element={<TrashedServices />} />
              <Route path="blogs" element={<Blogs />} />
              <Route path="blogs/create" element={<AddBlog />} />
              <Route path="blogs/:id/edit" element={<EditBlog />} />
              <Route path="blogs/trash" element={<TrashedBlogs />} />
              <Route path="blogs/:id/analytics" element={<BlogAnalytics />} />
              <Route path="blog-categories" element={<BlogCategories />} />
              <Route path="blog-categories/create" element={<AddBlogCategory />} />
              <Route path="blog-categories/:id/edit" element={<EditBlogCategory />} />
              <Route path="blog-categories/trash" element={<TrashedBlogCategories />} />
              <Route path="skills" element={<Skills />} />
              <Route path="skills/create" element={<AddSkill />} />
              <Route path="skills/:id/edit" element={<EditSkill />} />
              <Route path="skills/trash" element={<TrashedSkills />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="faqs/create" element={<AddFAQ />} />
              <Route path="faqs/:id/edit" element={<EditFAQ />} />
              <Route path="faqs/trash" element={<TrashedFAQs />} />
              <Route path="testimonials" element={<Testimonials />} />
              <Route path="testimonials/create" element={<AddTestimonial />} />
              <Route path="testimonials/:id/edit" element={<EditTestimonial />} />
              <Route path="testimonials/trash" element={<TrashedTestimonials />} />
              <Route path="header-footer" element={<HeaderFooter />} />
              <Route path="header-footer/create" element={<AddHeaderFooter />} />
              <Route path="header-footer/:id/edit" element={<EditHeaderFooter />} />
              <Route path="header-footer/trash" element={<TrashedHeaderFooter />} />
              <Route path="seo-settings" element={<SEOSettings />} />
              <Route path="seo-settings/:id/edit" element={<EditSEOSetting />} />
              <Route path="notification-settings" element={<NotificationSettings />} />
              <Route path="terms-conditions" element={<TermConditions />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="settings" element={<Settings />} />
              <Route path="work-education" element={<WorkEducation />} />
              <Route path="work-education/create" element={<AddWorkEducation />} />
              <Route path="work-education/:id/edit" element={<EditWorkEducation />} />
              <Route path="work-education/trash" element={<TrashedWorkEducation />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/create" element={<AddClient />} />
              <Route path="clients/:id/edit" element={<EditClient />} />
              <Route path="clients/trash" element={<TrashedClients />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/create" element={<AddProject />} />
              <Route path="projects/:id/edit" element={<EditProject />} />
              <Route path="projects/trash" element={<TrashedProjects />} />
              <Route path="newsletter-subscribers" element={<NewsletterSubscribers />} />
              <Route path="newsletter-subscribers/analytics" element={<NewsletterAnalytics />} />
              <Route path="jobs" element={<JobListPage />} />
              <Route path="/access-denied" element={<AccessDeniedPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;